// features/admin/paymentMethodsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface PaymentMethod {
  id: number;
  method_name: string;
  code: string;
  status: 0 | 1;
  extra_charge: string | number;
  createddt: string;
  updateddt: string | null;
}

// ── List ──────────────────────────────────────────────────────────────────────

export interface ListPaymentMethodsResponse {
  error: boolean;
  message: string;
  count: number;
  data: PaymentMethod[];
}

// ── Add ───────────────────────────────────────────────────────────────────────

export interface AddPaymentMethodRequest {
  method_name: string;
  code: string;
  status?: 0 | 1;
  extra_charge?: number;
}

export interface AddPaymentMethodResponse {
  error: boolean;
  message: string;
  recId?: number;
}

// ── Update ────────────────────────────────────────────────────────────────────

export interface UpdatePaymentMethodRequest {
  id: number;
  method_name?: string;
  code?: string;
  status?: 0 | 1;
  extra_charge?: number;
}

export interface UpdatePaymentMethodResponse {
  error: boolean;
  message: string;
}

// ── Delete ────────────────────────────────────────────────────────────────────

export interface DeletePaymentMethodRequest {
  id: number;
}

export interface DeletePaymentMethodResponse {
  error: boolean;
  message: string;
}

// ─── API Slice ────────────────────────────────────────────────────────────────

export const cashOnDeliveryApi = createApi({
  reducerPath: 'cashOnDeliveryApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['PaymentMethods'],
  endpoints: (build) => ({

    // ── List all payment methods ──
    listPaymentMethods: build.query<ListPaymentMethodsResponse, void>({
      query: () => ({
        url: '/admin/list',
        method: 'POST',
        body: new URLSearchParams({}).toString(),
      }),
      providesTags: ['PaymentMethods'],
      transformResponse: (response: any): ListPaymentMethodsResponse => ({
        error: response?.error ?? false,
        message: response?.message || '',
        count: response?.count || 0,
        data: Array.isArray(response?.data) ? response.data : [],
      }),
    }),

    // ── Add payment method ──
    addPaymentMethod: build.mutation<AddPaymentMethodResponse, AddPaymentMethodRequest>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('method_name', body.method_name);
        formData.append('code', body.code.toUpperCase());
        if (body.status !== undefined) formData.append('status', String(body.status));
        if (body.extra_charge !== undefined) formData.append('extra_charge', String(body.extra_charge));
        
        return {
          url: '/admin/add',
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: ['PaymentMethods'],
      transformResponse: (response: any): AddPaymentMethodResponse => ({
        error: response?.error ?? false,
        message: response?.message || 'Payment method added successfully',
        recId: response?.recId,
      }),
    }),

    // ── Update payment method ──
    updatePaymentMethod: build.mutation<UpdatePaymentMethodResponse, UpdatePaymentMethodRequest>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('id', String(body.id));
        if (body.method_name) formData.append('method_name', body.method_name);
        if (body.code) formData.append('code', body.code.toUpperCase());
        if (body.status !== undefined) formData.append('status', String(body.status));
        if (body.extra_charge !== undefined) formData.append('extra_charge', String(body.extra_charge));
        
        return {
          url: '/admin/update',
          method: 'POST',
          body: formData.toString(),
        };
      },
      invalidatesTags: ['PaymentMethods'],
      transformResponse: (response: any): UpdatePaymentMethodResponse => ({
        error: response?.error ?? false,
        message: response?.message || 'Payment method updated successfully',
      }),
    }),

    // ── Delete payment method ──
    deletePaymentMethod: build.mutation<DeletePaymentMethodResponse, DeletePaymentMethodRequest>({
      query: (body) => ({
        url: '/admin/delete',
        method: 'POST',
        body: new URLSearchParams({ id: String(body.id) }).toString(),
      }),
      invalidatesTags: ['PaymentMethods'],
      transformResponse: (response: any): DeletePaymentMethodResponse => ({
        error: response?.error ?? false,
        message: response?.message || 'Payment method deleted successfully',
      }),
    }),

  }),
});

export const {
  useListPaymentMethodsQuery,
  useAddPaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useDeletePaymentMethodMutation,
} = cashOnDeliveryApi;